/**
 * Worksmart AI Service
 * 
 * Handles fetching and processing of Worksmart AI report data from Lambda backend.
 * Integrates with comprehensive team data to enrich Worksmart data with email addresses.
 */

import { 
  WorksmartAIReportResponse, 
  WorksmartAIMemberData, 
  WorksmartAIRequest,
  WorksmartTeamStats
} from '../types/worksmartTypes';
import { CrossoverTeamMemberActivity } from '../types';
import { getApiEndpoint } from '../config/amplifyConfig';

/**
 * Fetch Worksmart AI report data for a specific week
 * 
 * @param weekDate - Monday date in YYYY-MM-DD format
 * @param centralPSUserIds - Optional: array of xo_user_ids to filter (Central PS users)
 * @param token - Authentication token
 * @returns Promise<WorksmartAIReportResponse>
 */
export async function fetchWorksmartAIData(
  weekDate: string,
  centralPSUserIds?: number[],
  token?: string,
  managerId?: number
): Promise<WorksmartAIReportResponse> {
  const API_ENDPOINT = getApiEndpoint('/worksmart-ai-report');
  
  console.log('🔍 [WORKSMART-AI] Fetching Worksmart AI data:', {
    weekDate,
    userCount: centralPSUserIds?.length || 'all',
    hasToken: !!token,
    managerId: managerId || 'not-provided',
    sampleUserIds: centralPSUserIds?.slice(0, 5)
  });
  console.log('🌐 [WORKSMART-AI] Using endpoint:', API_ENDPOINT);

  const requestBody: WorksmartAIRequest = {
    weekDate,
    centralPSUserIds,
    managerId
  };
  
  console.log('📤 [WORKSMART-AI] Request body:', JSON.stringify(requestBody, null, 2));

  const headers: HeadersInit = {
    'Content-Type': 'application/json'
  };

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  try {
    const response = await fetch(API_ENDPOINT, {
      method: 'POST',
      headers,
      body: JSON.stringify(requestBody)
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('🚨 [WORKSMART-AI] API Error:', {
        status: response.status,
        statusText: response.statusText,
        body: errorText
      });
      throw new Error(`Worksmart AI API error: ${response.status} - ${errorText}`);
    }

    const data: WorksmartAIReportResponse = await response.json();
    
    console.log('✅ [WORKSMART-AI] Data fetched successfully:', {
      totalUsers: data.summary.totalUsers,
      cached: data.summary.cached,
      averageAIRatio: data.summary.averageAIRatioPercent.toFixed(2) + '%'
    });

    return data;
  } catch (error) {
    console.error('🚨 [WORKSMART-AI] Error fetching Worksmart AI data:', error);
    throw error;
  }
}

/**
 * Process and enrich Worksmart data with Crossover team data
 * Match by xo_user_id to add email and team context
 * 
 * @param worksmartData - Raw Worksmart AI data
 * @param teamMembers - Crossover team member activities
 * @returns Enriched Worksmart data with emails
 */
export function enrichWorksmartDataWithTeamContext(
  worksmartData: WorksmartAIMemberData[],
  teamMembers: CrossoverTeamMemberActivity[]
): WorksmartAIMemberData[] {
  console.log('🔗 [WORKSMART-AI] Enriching Worksmart data with team context:', {
    worksmartUsers: worksmartData.length,
    teamMembers: teamMembers.length
  });

  // Build map of xo_user_id to email from team data
  const userIdToEmailMap = new Map<number, string>();
  
  teamMembers.forEach(member => {
    // Extract user ID and email from team member data
    // For Worksmart AI: Use candidate.userId (matches xo.user_id in Worksmart database)
    let userId = (member as any).assignment?.candidate?.userId;
    
    // If not found, try deep nested path
    if (!userId) {
      userId = (member as any).assignment?.selection?.marketplaceMember?.application?.candidate?.userId;
    }
    
    // Try multiple paths for email
    const email = (member as any).assignmentHistory?.companyEmail || 
                  (member as any).assignment?.candidate?.email ||
                  (member as any).assignment?.selection?.marketplaceMember?.application?.candidate?.email;
    
    if (userId && userId !== 0 && email) {
      userIdToEmailMap.set(userId, email);
    }
  });

  console.log('📧 [WORKSMART-AI] Built user ID to email map:', userIdToEmailMap.size, 'entries');

  // Enrich Worksmart data with emails
  const enrichedData = worksmartData.map(user => ({
    ...user,
    email: userIdToEmailMap.get(user.xoUserId) || user.email
  }));

  const enrichedCount = enrichedData.filter(u => u.email).length;
  console.log('✅ [WORKSMART-AI] Enriched data:', {
    total: enrichedData.length,
    withEmail: enrichedCount,
    withoutEmail: enrichedData.length - enrichedCount
  });

  return enrichedData;
}

/**
 * Calculate team-level statistics for Worksmart AI data
 * 
 * @param data - Array of Worksmart AI member data
 * @returns Team statistics
 */
export function calculateWorksmartTeamStats(
  data: WorksmartAIMemberData[]
): WorksmartTeamStats {
  if (data.length === 0) {
    return {
      totalUsers: 0,
      averageAIRatio: 0,
      averageAIRatioPercent: 0,
      highestAIUser: null,
      lowestAIUser: null
    };
  }

  // Calculate average AI ratio
  const totalAIRatio = data.reduce((sum, user) => sum + user.aiRatio, 0);
  const averageAIRatio = totalAIRatio / data.length;

  // Find highest and lowest AI adopters
  const sortedByAI = [...data].sort((a, b) => b.aiRatio - a.aiRatio);
  const highestAIUser = sortedByAI[0];
  const lowestAIUser = sortedByAI[sortedByAI.length - 1];

  return {
    totalUsers: data.length,
    averageAIRatio,
    averageAIRatioPercent: averageAIRatio * 100,
    highestAIUser: highestAIUser ? {
      name: highestAIUser.name,
      email: highestAIUser.email,
      aiRatio: highestAIUser.aiRatio
    } : null,
    lowestAIUser: lowestAIUser ? {
      name: lowestAIUser.name,
      email: lowestAIUser.email,
      aiRatio: lowestAIUser.aiRatio
    } : null
  };
}

/**
 * Sort Worksmart AI data by a specific field
 * 
 * @param data - Array of Worksmart AI member data
 * @param sortBy - Field to sort by
 * @param ascending - Sort order (default: false/descending)
 * @returns Sorted data
 */
export function sortWorksmartData(
  data: WorksmartAIMemberData[],
  sortBy: keyof WorksmartAIMemberData,
  ascending: boolean = false
): WorksmartAIMemberData[] {
  const sorted = [...data].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    // Handle string comparison
    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return ascending 
        ? aValue.localeCompare(bValue) 
        : bValue.localeCompare(aValue);
    }

    // Handle numeric comparison
    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return ascending ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  return sorted;
}

/**
 * Get AI adoption level label and color based on ratio
 * 
 * @param aiRatioPercent - AI ratio as percentage (0-100)
 * @returns Object with label and color
 */
export function getAIAdoptionLevel(aiRatioPercent: number): { label: string; color: string } {
  if (aiRatioPercent >= 75) {
    return { label: 'Leaders', color: '#22c55e' }; // Green
  } else if (aiRatioPercent >= 50) {
    return { label: 'Consistent Progress', color: '#eab308' }; // Yellow
  } else if (aiRatioPercent >= 30) {
    return { label: 'Building Momentum', color: '#f97316' }; // Orange
  } else {
    return { label: 'Getting Started', color: '#ef4444' }; // Red
  }
}

/**
 * Format hours for display
 * 
 * @param hours - Hours value
 * @returns Formatted string (e.g., "28.5h")
 */
export function formatHours(hours: number): string {
  return `${hours.toFixed(1)}h`;
}

export default {
  fetchWorksmartAIData,
  enrichWorksmartDataWithTeamContext,
  calculateWorksmartTeamStats,
  sortWorksmartData,
  getAIAdoptionLevel,
  formatHours
};

